import {
  Button,
  JaneGoldIcon,
  TagFilledIcon,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useCallback } from 'react';

import {
  useFilterSearchParams,
  useSearchParamFilter,
} from '@jane/bloom/hooks/filters';
import type { ProductSearchFilterFacet } from '@jane/bloom/viewModels/productSearchFilters';

import { ModalSection } from './modalSection';

import styles from './buttonFilterSection.module.css';

interface DealsAndRewardsSection {
  /** Omitted when Jane Gold is not offered here at all; a count of 0 renders it disabled. */
  goldItem?: ProductSearchFilterFacet;
  saleItem?: ProductSearchFilterFacet;
}

export function DealsAndRewardsSection({
  goldItem,
  saleItem,
}: DealsAndRewardsSection) {
  const [searchParams, setSearchParams] = useFilterSearchParams();

  const { getFilterStatus, setFilter } = useSearchParamFilter('categories');

  const hasBrandDiscount = searchParams.has('hasBrandDiscount');

  const toggleJaneGold = useCallback(() => {
    setSearchParams((prev) => {
      if (hasBrandDiscount) {
        prev.delete('hasBrandDiscount');
      } else {
        prev.set('hasBrandDiscount', '');
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const toggleSale = useCallback(() => {
    const currentValue = getFilterStatus('sale') === 'selected';
    setFilter('sale', currentValue === undefined ? true : !currentValue);
  }, [getFilterStatus, setFilter]);

  const deselectSaleAndGold = useCallback(async () => {
    setSearchParams((prev) => {
      prev.delete('hasBrandDiscount');
      // Clear the 'sale' category filter
      const categories = prev
        .getAll('category')
        .filter((cat) => cat !== 'sale');
      prev.delete('category');
      categories.forEach((cat) => prev.append('category', cat));
      return prev;
    });
  }, [setSearchParams]);

  return (
    <ModalSection
      filterLabel="Deals and rewards"
      shouldShowDeselectOption={true}
      deselectFilters={deselectSaleAndGold}
    >
      <div className={clsx(styles['buttons'], styles['buttonGrid--3'])}>
        {goldItem && (
          <Button
            key="jane-gold"
            disabled={goldItem.count === 0}
            size="large"
            full
            alignment="left"
            startIcon={<JaneGoldIcon color="gold" />}
            onClick={toggleJaneGold}
            variant={hasBrandDiscount ? 'tertiary-selected' : 'tertiary'}
            label={
              <Typography variant="body-semibold" color="inherit">
                {goldItem.label} ({goldItem.count})
              </Typography>
            }
          />
        )}
        {saleItem && (
          <Button
            key="sale"
            disabled={saleItem.count === 0}
            size="large"
            full
            alignment="left"
            startIcon={<TagFilledIcon color="info" />}
            onClick={toggleSale}
            label={`${saleItem.label} (${saleItem.count})`}
            variant={
              getFilterStatus(saleItem.value) === 'selected'
                ? 'tertiary-selected'
                : 'tertiary'
            }
          />
        )}
      </div>
    </ModalSection>
  );
}
